import { useEffect, useRef } from 'react';
import { layoutServiceFactory } from '../lib/layout-service-factory';
import { getSiteNameFromHost, applySiteShellClass } from '../helpers/siteHelpers';
import config from '../temp/config';

// When Sitecore can't resolve an item it returns `route: null` AND a stripped-down
// context containing only `language` and `pageEditing` — `globalContent`, `site` and
// `trademarkReplacements` are all dropped, because the context extension that supplies
// them only runs for a resolved item. That leaves the 404 page with no navigation,
// no footer links and no site identity.
//
// The `/notfound` item itself resolves normally on every site, so we re-fetch its
// layout data and merge the context back in, keeping `route` null so RouteHandler
// continues to render the 404 component rather than a real page.

const FALLBACK_ITEM_PATH = '/notfound';

// The generated config points at the `default` configuration, which answers 200 but
// omits globalContent — the context extensions are only registered on `jss`, which is
// what jssProxy uses server-side. Asking for `default` here silently returns nothing
// useful, so pin it.
const LAYOUT_SERVICE_CONFIGURATION = 'jss';

/**
 * @param {object} sitecoreContext - current context from `withSitecoreContext`
 * @param {function} updateSitecoreContext - requires `withSitecoreContext({ updatable: true })`
 */
export default function useFallbackGlobalContent(sitecoreContext, updateSitecoreContext) {
  // Read the latest values inside the effect without making them dependencies —
  // the effect updates the very context it would otherwise re-run on.
  const latest = useRef({ sitecoreContext, updateSitecoreContext });
  latest.current = { sitecoreContext, updateSitecoreContext };

  const language = sitecoreContext?.language || config.defaultLanguage;
  const needsFallback = !sitecoreContext?.globalContent;

  useEffect(() => {
    if (!needsFallback) return undefined;

    let cancelled = false;
    const siteName = getSiteNameFromHost();

    layoutServiceFactory
      .create(siteName, LAYOUT_SERVICE_CONFIGURATION)
      .fetchLayoutData(FALLBACK_ITEM_PATH, language)
      .then((data) => {
        if (cancelled) return;

        const fallbackContext = data?.sitecore?.context;
        if (!fallbackContext?.globalContent) return;

        // The shell class is written by the server outside the React tree, so it has to
        // be corrected directly or all the site-scoped styling stays on the wrong site.
        applySiteShellClass(fallbackContext.site?.name || siteName);

        const { sitecoreContext: current, updateSitecoreContext: update } = latest.current;
        if (!update) return;

        // setContext replaces rather than merges, so spread the existing value first.
        // `route` stays null so RouteHandler keeps rendering the 404.
        update({ ...current, ...fallbackContext, route: null });
      })
      .catch(() => {
        // Nothing to recover — the 404 still renders, just without global chrome.
      });

    return () => {
      cancelled = true;
    };
  }, [needsFallback, language]);
}
